class ReviewsCarousel {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.currentPosition = 0;
    this.isDragging = false;
    this.startX = 0;
    this.startLeft = 0;
    this.isMobile = window.innerWidth <= 768;
    this.scrollbarVisible = false;
    this.init();
  }

  init() {
    const root = document.querySelector(`#shopify-section-${this.sectionId}`);
    if (!root) {
      console.warn(`Reviews carousel: No se encontró la sección ${this.sectionId}`);
      return;
    }
    
    this.sectionEl = root.querySelector('.reviews-section');
    if (!this.sectionEl) return;

    this.carouselTrack = this.sectionEl.querySelector('.reviews-carousel-track');
    this.slides = this.sectionEl.querySelectorAll('.carousel-slide');
    this.scrollbarDrag = this.sectionEl.querySelector('.carousel-scrollbar-drag');
    this.scrollbar = this.sectionEl.querySelector('.reviews-carousel-scrollbar');
    this.carouselWrapper = this.sectionEl.querySelector('.reviews-carousel-wrapper');
    this.scrollbarWrapper = this.sectionEl.querySelector('.reviews-carousel-scrollbar-wrapper');
    
    if (!this.carouselTrack || this.slides.length === 0) {
      console.warn('Reviews carousel: No se encontraron slides');
      return;
    }
    
    this.setupMobileBehavior();
    
    if (!this.isMobile) {
      this.calculateDimensions();
      this.bindEvents();
      this.updateScrollbarVisibility();
    } else {
      this.initMobileTouchScroll();
    }
    
    this.initVideoModal();
    
    this.handleResize = () => {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 768;
      
      if (wasMobile !== this.isMobile) {
        this.setupMobileBehavior();
        
        if (!this.isMobile && !this.eventsBound) {
          this.calculateDimensions();
          this.bindEvents();
          this.updateScrollbarVisibility();
          this.eventsBound = true;
        } else if (this.isMobile) {
          this.initMobileTouchScroll();
        }
      }
      
      if (!this.isMobile) {
        this.calculateDimensions();
        this.updateScrollbarVisibility();
      }
    };
    
    window.addEventListener('resize', this.handleResize);
  }

  setupMobileBehavior() {
    if (!this.carouselTrack) return;
    
    if (this.isMobile) {
      this.carouselTrack.style.transform = 'none';
      this.carouselTrack.style.transition = 'none';
      
      if (this.scrollbarWrapper) {
        this.scrollbarWrapper.style.display = 'none';
        this.scrollbarWrapper.classList.remove('visible');
      }
      
      if (this.carouselWrapper) {
        this.carouselWrapper.style.overflowX = 'auto';
        this.carouselWrapper.style.cursor = 'grab';
      }
    } else {
      if (this.carouselWrapper) {
        this.carouselWrapper.style.overflowX = 'hidden';
      }
    }
  }

  initMobileTouchScroll() {
    if (!this.isMobile || !this.carouselWrapper) return;
    
    let startX = 0;
    let startY = 0;
    let scrollLeft = 0;
    let isScrolling = false;
    let isVerticalScroll = false;
    
    const onTouchStart = (e) => {
      startX = e.touches[0].pageX - this.carouselWrapper.offsetLeft;
      startY = e.touches[0].pageY;
      scrollLeft = this.carouselWrapper.scrollLeft;
      isScrolling = true;
      isVerticalScroll = false;
      this.carouselWrapper.style.cursor = 'grabbing';
    };
    
    const onTouchMove = (e) => {
      if (!isScrolling) return;
      
      const x = e.touches[0].pageX - this.carouselWrapper.offsetLeft;
      const y = e.touches[0].pageY;
      
      const deltaX = x - startX;
      const deltaY = y - startY;
      
      if (!isVerticalScroll) {
        if (Math.abs(deltaY) > Math.abs(deltaX)) {
          isVerticalScroll = true;
          return;
        }
        
        e.preventDefault();
      }
      
      if (!isVerticalScroll) {
        const walk = deltaX * 2;
        this.carouselWrapper.scrollLeft = scrollLeft - walk;
      }
    };
    
    const onTouchEnd = () => {
      isScrolling = false;
      isVerticalScroll = false;
      this.carouselWrapper.style.cursor = 'grab';
    };
    
    this.carouselWrapper.removeEventListener('touchstart', onTouchStart);
    this.carouselWrapper.removeEventListener('touchmove', onTouchMove);
    this.carouselWrapper.removeEventListener('touchend', onTouchEnd);
    
    this.carouselWrapper.addEventListener('touchstart', onTouchStart, { passive: true });
    this.carouselWrapper.addEventListener('touchmove', onTouchMove, { passive: false });
    this.carouselWrapper.addEventListener('touchend', onTouchEnd);
  }

  calculateDimensions() {
    if (this.slides.length === 0 || this.isMobile) return;
    
    const trackStyle = window.getComputedStyle(this.carouselTrack);
    const gap = parseInt(trackStyle.gap) || 20;
    
    this.slideWidth = this.slides[0].offsetWidth + gap;
    const carouselWidth = this.sectionEl.querySelector('.reviews-carousel').offsetWidth;
    this.visibleSlides = Math.floor(carouselWidth / this.slideWidth);
    this.maxScroll = Math.max(0, (this.slides.length - this.visibleSlides) * this.slideWidth);
    
    const shouldCenter = this.sectionEl.classList.contains('reviews-section--centered') && 
                        this.slides.length * this.slideWidth <= carouselWidth;
    
    if (shouldCenter) {
      this.carouselTrack.classList.add('centered');
      this.currentPosition = 0;
    } else {
      this.carouselTrack.classList.remove('centered');
    }
    
    this.currentPosition = Math.min(this.currentPosition, this.maxScroll);
    
    if (!this.isMobile) {
      this.scrollTo(this.currentPosition, false);
    }
  }

  bindEvents() {
    if (this.isMobile) return;
    
    this.eventsBound = true;

    if (this.scrollbarDrag && this.scrollbarVisible) {
      this.initScrollbar();
    }
    
    if (this.carouselWrapper) {
      this.carouselWrapper.addEventListener('wheel', (e) => this.handleWheel(e), { passive: false });
    }

    if (this.scrollbar && this.scrollbarVisible) {
      this.scrollbar.addEventListener('click', (e) => this.handleScrollbarClick(e));
    }
  }

  scrollTo(position, animate = true) {
    if (this.isMobile) return;
    
    this.currentPosition = Math.max(0, Math.min(position, this.maxScroll));
    
    if (this.carouselTrack) {
      if (animate) {
        this.carouselTrack.style.transition = 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      } else {
        this.carouselTrack.style.transition = 'none';
      }
      this.carouselTrack.style.transform = `translateX(-${this.currentPosition}px)`;
    }
    
    this.updateScrollbar();
  }

  updateScrollbarVisibility() {
    if (this.isMobile || !this.scrollbarWrapper) return;
    
    const needsScrollbar = this.maxScroll > 0;
    
    if (needsScrollbar && !this.scrollbarVisible) {
      this.scrollbarWrapper.classList.add('visible');
      this.scrollbarVisible = true;
      this.initScrollbar();
    } else if (!needsScrollbar && this.scrollbarVisible) {
      this.scrollbarWrapper.classList.remove('visible');
      this.scrollbarVisible = false;
    }
  }

  updateScrollbar() {
    if (this.isMobile || !this.scrollbarDrag || !this.scrollbar || this.maxScroll <= 0) return;
    
    const scrollPercentage = this.currentPosition / this.maxScroll;
    const scrollbarWidth = this.scrollbar.offsetWidth;
    const dragWidth = Math.max(50, scrollbarWidth * (this.visibleSlides / this.slides.length));
    const maxDragPosition = scrollbarWidth - dragWidth;
    const dragPosition = scrollPercentage * maxDragPosition;
    
    this.scrollbarDrag.style.width = `${dragWidth}px`;
    this.scrollbarDrag.style.left = `${dragPosition}px`;
  }

  initScrollbar() {
    if (this.isMobile || !this.scrollbarVisible) return;
    
    const onDrag = (e) => {
      if (!this.isDragging) return;
      e.preventDefault();
      
      const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
      const deltaX = clientX - this.startX;
      const scrollbarWidth = this.scrollbar.offsetWidth;
      const dragWidth = this.scrollbarDrag.offsetWidth;
      const newLeft = Math.max(0, Math.min(this.startLeft + deltaX, scrollbarWidth - dragWidth));
      
      this.scrollbarDrag.style.left = `${newLeft}px`;
      
      const scrollPercentage = newLeft / (scrollbarWidth - dragWidth);
      this.scrollTo(scrollPercentage * this.maxScroll, true);
    };
    
    const stopDrag = () => {
      this.isDragging = false;
      document.removeEventListener('mousemove', onDrag);
      document.removeEventListener('mouseup', stopDrag);
      document.removeEventListener('touchmove', onDrag);
      document.removeEventListener('touchend', stopDrag);
      
      document.body.style.cursor = '';
      if (this.scrollbarDrag) {
        this.scrollbarDrag.style.cursor = '';
      }
    };
    
    this.scrollbarDrag.addEventListener('mousedown', (e) => {
      this.isDragging = true;
      this.startX = e.clientX;
      this.startLeft = parseFloat(this.scrollbarDrag.style.left) || 0;
      
      document.body.style.cursor = 'grabbing';
      this.scrollbarDrag.style.cursor = 'grabbing';
      
      document.addEventListener('mousemove', onDrag);
      document.addEventListener('mouseup', stopDrag);
    });
  }

  handleScrollbarClick(e) {
    if (this.isMobile || !this.scrollbarVisible || e.target === this.scrollbarDrag) return;
    
    const scrollbarRect = this.scrollbar.getBoundingClientRect();
    const clickX = e.clientX - scrollbarRect.left;
    const scrollbarWidth = this.scrollbar.offsetWidth;
    const dragWidth = this.scrollbarDrag.offsetWidth;
    
    let newLeft = clickX - (dragWidth / 2);
    newLeft = Math.max(0, Math.min(newLeft, scrollbarWidth - dragWidth));
    
    this.scrollbarDrag.style.left = `${newLeft}px`;
    
    const scrollPercentage = newLeft / (scrollbarWidth - dragWidth);
    this.scrollTo(scrollPercentage * this.maxScroll, true);
  }

  handleWheel(e) {
    if (this.isMobile) return;
    
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      e.preventDefault();
      this.scrollTo(this.currentPosition + e.deltaX * 1.5, true);
    }
  }

  initVideoModal() {
    this.modal = this.sectionEl.querySelector('.video-modal');
    this.modalContent = this.modal?.querySelector('.video-modal-content');
    this.closeBtn = this.modal?.querySelector('.video-modal-close');
    
    if (!this.modal || !this.modalContent) return;
    
    this.slides.forEach(slide => {
      const imageWrapper = slide.querySelector('.reviews-item-image-wrapper');
      const videoButton = slide.querySelector('.reviews-item-video-button');
      
      if (!imageWrapper) return;

      imageWrapper.addEventListener('click', (evt) => {
        if (evt.target === videoButton || videoButton?.contains(evt.target)) {
          return;
        }
        
        evt.preventDefault();
        evt.stopPropagation();
        this.openVideoFromSlide(slide);
      });

      if (videoButton) {
        videoButton.addEventListener('click', (evt) => {
          evt.preventDefault();
          evt.stopPropagation();
          this.openVideoFromSlide(slide);
        });
      }
    });

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.closeModal());
    }
    
    if (this.modal) {
      this.modal.addEventListener('click', (evt) => {
        if (evt.target === this.modal) this.closeModal();
      });
    }
    
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal && this.modal.classList.contains('active')) {
        this.closeModal();
      }
    });
  }

  openVideoFromSlide(slide) {
    const hiddenVideo = slide.querySelector('.reviews-item-hidden-video video');
    if (hiddenVideo) {
      this.openModalWithVideoNode(hiddenVideo);
      return;
    }

    const fallback = slide.getAttribute('data-video-fallback') || '';
    if (fallback) {
      this.openModalWithFallbackSrc(fallback);
      return;
    }

    console.warn('Reviews carousel: No video disponible para este slide');
  }

  openModalWithVideoNode(videoNode) {
    if (!this.modal || !this.modalContent || !videoNode) return;

    this.closeModal();

    const clone = videoNode.cloneNode(true);

    clone.removeAttribute('hidden');
    clone.removeAttribute('style');
    clone.setAttribute('playsinline', '');
    clone.controls = true;
    
    try { 
      clone.muted = false; 
      clone.autoplay = true; 
    } catch(e) { 
      console.warn('Reviews carousel: No se pudieron configurar las propiedades del video', e);
    }

    clone.style.width = 'auto';
    clone.style.height = 'auto';
    clone.style.maxWidth = '100%';
    clone.style.maxHeight = '80vh';
    clone.style.objectFit = 'contain';
    clone.style.display = 'block';
    clone.style.borderRadius = '0';
    clone.style.boxShadow = '0 10px 40px rgba(0, 0, 0, 0.3)';

    this.modalContent.appendChild(clone);
    this.modal.classList.add('active');
    this.modal.setAttribute('aria-hidden', 'false');
    
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.body.classList.add('video-modal-open');

    const playPromise = clone.play();
    if (playPromise !== undefined) {
      playPromise.catch(function(err) {
        console.warn('Reviews carousel: El video no pudo reproducirse automáticamente', err);
      });
    }

    this.modal._currentVideo = clone;
  }

  openModalWithFallbackSrc(src) {
    if (!this.modal || !this.modalContent || !src) return;
    
    const videoElement = document.createElement('video');
    videoElement.setAttribute('playsinline', '');
    videoElement.controls = true;
    
    try { 
      videoElement.autoplay = true; 
      videoElement.muted = false; 
    } catch(e) { }
    
    videoElement.style.width = 'auto'; 
    videoElement.style.height = 'auto'; 
    videoElement.style.maxWidth = '100%';
    videoElement.style.maxHeight = '80vh';
    videoElement.style.objectFit = 'contain';
    videoElement.style.borderRadius = '0';
    videoElement.style.boxShadow = '0 10px 40px rgba(0, 0, 0, 0.3)';
    
    const sourceElement = document.createElement('source');
    sourceElement.src = src;
    sourceElement.type = 'video/mp4';
    videoElement.appendChild(sourceElement);
    
    this.openModalWithVideoNode(videoElement);
  }

  closeModal() {
    if (!this.modal) return;
    
    try {
      if (this.modal._currentVideo) {
        this.modal._currentVideo.pause();
        this.modal._currentVideo.remove();
      }
    } catch(e) {
      console.warn('Reviews carousel: Error al cerrar el modal', e);
    }
    
    this.modal._currentVideo = null;
    
    if (this.modalContent) {
      this.modalContent.innerHTML = '';
    }
    
    this.modal.classList.remove('active');
    this.modal.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    document.body.classList.remove('video-modal-open');
  }

  destroy() {
    if (this.modal && this.modal._currentVideo) {
      this.closeModal();
    }
    
    window.removeEventListener('resize', this.handleResize);
  }
}

document.addEventListener('DOMContentLoaded', function() {
  setTimeout(() => {
    document.querySelectorAll('[data-section-type="reviews-carousel"]').forEach(section => {
      const sectionId = section.dataset.sectionId;
      if (sectionId) {
        try {
          new ReviewsCarousel(sectionId);
        } catch (error) {
          console.error('Error inicializando ReviewsCarousel:', error);
        }
      }
    });
  }, 100);
});

window.ReviewsCarousel = ReviewsCarousel;